import type { ChatState, FiltersState } from './interfaces';
import { generateEmptyChatSessionFiltersState } from './state.initial-state';

/**
 * Adds the tags in `current` that the user cannot remove (e.g. the segment applied from the page) to
 * `defaults`, so resetting only clears user-editable filters.
 */
export function withLockedTags(defaults: FiltersState.State, current: FiltersState.State): FiltersState.State {
  const lockedTags = (current.tags ?? []).filter((t) => t.metadata?.canRemove === false);
  if (!lockedTags.length) {
    return defaults;
  }
  return { ...defaults, tags: [...(defaults.tags ?? []), ...lockedTags] };
}

export function filtersReducer({
  settings,
  session: state,
}: ChatState.State, action: ChatState.Action): FiltersState.State {

  switch (action.type) {
    case 'filters/reset': {
      const context = state.chatInstance.context;
      return withLockedTags(generateEmptyChatSessionFiltersState({ context, features: settings }), state.filters);
    }
    case 'filters/updated': {
      return Object.fromEntries(Object.entries({ ...state.filters, ...action.payload.filters }).filter(([_, v]) => v != null));
    }
    case 'filters/add-custom-filter': {
      const customFilter = action.payload.value;
      const hasDuplicateCustomFilter = state.filters.custom.some((existingFilter) => {
        return JSON.stringify(existingFilter) === JSON.stringify(customFilter);
      });

      if (hasDuplicateCustomFilter) {
        return state.filters;
      }

      return { ...state.filters, custom: state.filters.custom.concat(customFilter) };
    }
    case 'filters/remove-custom-filter': {
      return { ...state.filters, custom: state.filters.custom.filter((_, i) => i !== action.payload.id) };
    }
    case 'filters/update-custom-filter': {
      return { ...state.filters, custom: state.filters.custom.map((f, i) => i === action.payload.id ? action.payload.value : f) };
    }

    case 'chat-loaded':
      {
        const defaults = generateEmptyChatSessionFiltersState({
          context: action.payload.chatInstance.context,
          features: settings,
        });

        return {
          ...defaults,
          ...(action.payload.chatInstance.filters ?? {}),
          ...action.payload.chatInstance.filterOverrides,
        };
      }

    case 'query-response-available': {
      const emptyState = generateEmptyChatSessionFiltersState({
        context: state.chatInstance.context,
        features: settings,
      });
      const mergedState = {
        ...emptyState,
        ...(action.payload.chatInstance.filters ?? {}),
        ...action.payload.chatInstance.filterOverrides,
      };

      const immutableTags = (state.filters.tags ?? []).filter((t) => t.metadata?.canRemove === false);
      const mergedTags = [...(mergedState.tags ?? [])];
      for (const immutableTag of immutableTags) {
        const hasImmutableTag = mergedTags.some(
          (existingTag) => JSON.stringify(existingTag) === JSON.stringify(immutableTag)
        );
        if (!hasImmutableTag) {
          mergedTags.push(immutableTag);
        }
      }

      return {
        ...mergedState,
        ...(mergedTags.length ? { tags: mergedTags } : {}),
      };
    }

    default:
      return state.filters;
  }
}